import { FC, useEffect } from "react";

import { CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { Listbox } from "mds/components/Listbox";
import { ORDER_BY_CODE } from "mds/hooks/use-ordered";
import { TextBooksIcon } from "mds/icons";
import { storeApi } from "store/index";
import { courseSelectors, selectAdminAccessibleCourses } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { getCourseDisplayTitle } from "utils/course";

export type CourseMenuProps = {
  selectedCourseId: string | null;
  onCourseChange: (courseId: string) => void;
  defaultToAll?: boolean;
  adminAccessibleOnly?: boolean;
  showEmptyOption?: boolean;
  size?: "xs" | "s";
  disabled?: boolean;
  filterBy?: (course: CourseType) => boolean;
  className?: string;
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
};

// This is very similar to TermMenu, consider refactoring to a shared general dropdown component
export const CourseMenu: FC<CourseMenuProps> = ({
  selectedCourseId,
  onCourseChange,
  defaultToAll = true,
  showEmptyOption = true,
  adminAccessibleOnly = false,
  size = "xs",
  disabled = false,
  filterBy,
  className,
  id,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  "aria-required": ariaRequired,
}) => {
  const allCourses = useListSelector(
    adminAccessibleOnly ? selectAdminAccessibleCourses : courseSelectors.selectAll,
    ["id", "code", "title"],
  ).toSorted(ORDER_BY_CODE);
  const courses = filterBy ? allCourses.filter(filterBy) : allCourses;
  const emptyCourseOption: CourseType = {
    id: "",
    code: defaultToAll ? t("import_modal.all_courses") : t("import_modal.select_course"),
    title: "",
    grouping_id: null,
  };
  const courseOptions = showEmptyOption ? [emptyCourseOption, ...courses] : courses;

  useEffect(() => {
    storeApi.courses.list();
  }, []);

  const listboxItems = courseOptions.map((courseOption) => ({
    id: courseOption.id,
    title: getCourseDisplayTitle(courseOption),
    iconStart: <TextBooksIcon />,
  }));

  return (
    <Listbox
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      aria-required={ariaRequired}
      buttonSize={size}
      className={className}
      disabled={disabled}
      id={id}
      items={listboxItems}
      selectedValue={selectedCourseId ?? ""}
      onSelectionChange={(item) => onCourseChange(item.id)}
    />
  );
};
